import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import secureLocalStorage from "react-secure-storage";
import { VARIABLES } from "../../Constant";
import { useAPIQuery } from "@dev2_techwalnut/tw-api-query-hook";
import { Table } from "@dev2_techwalnut/tw-react-crud-dashlite";
import moment from "moment";
import Error from "../../ErrorPages/Error";
import DeliveryStatusModal from "./DeliveryStatusModal";
import Swal from "sweetalert2";

const DeliveryStatus = () => {
    const userID = secureLocalStorage.getItem("userID");
    const privileges = secureLocalStorage.getItem("privileges");
    const delay_time = VARIABLES.delayTime;
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const initialEntries = Number(searchParams.get("entries")) || 10;
    const initialPage = Number(searchParams.get("page")) || 1;
    const initialQueries = searchParams.get("q")?.split(",") || [];
    const initialColumns = searchParams.get("c")?.split(",") || [];

    const [pageIndex, setPageIndex] = useState(initialPage - 1);
    const [entries, setEntries] = useState(initialEntries);
    const [globalFilterValue, setGlobalFilterValue] = useState("");
    const [columnFilterKey, setColumnFilterKey] = useState("");
    const [columnFilterValue, setColumnFilterValue] = useState("");

    const {
        useFetch: useFetchPurchaseOrders,
        useUpdate: useUpdatePurchaseOrders,
    } = useAPIQuery({
        key: "purchase-order",
        endpoint: "/api/purchase-order/",
    });
    const {
        data: purchaseOrders,
        isLoading: purchaseOrdersLoading,
        isError: errorLoadingPurchaseOrders,
        isFetching: fetchingPurchaseOrders,
    } = useFetchPurchaseOrders({
        params: {
            entries: entries,
            page_no: pageIndex + 1,
            q: globalFilterValue !== "" ? globalFilterValue : columnFilterValue,
            c: globalFilterValue === "" ? columnFilterKey : "",
        },
        abortOnNewRequest: true,
        refetchOnWindowFocus: false,
        staleTime: 1000 * 60 * 3,
        retry: 3,
        keepPlaceholder: true,
        refetchOnReconnect: true,
    });

    const { mutate: updatePO, isPending: updatingPO } =
        useUpdatePurchaseOrders();

    const TypeMasterQuery = useAPIQuery({
        key: "api/type-master",
        endpoint: "/api/type-master/",
    });

    const threshold = TypeMasterQuery.useFetch({
        params: {
            q: "PO - acceptance threshold",
            c: "category",
        },
    });

    const TypeMasterDeliveryStatus = TypeMasterQuery.useFetch({
        params: {
            q: "PO - Delivery status",
            c: "category",
        },
    });

    const columns = [
        {
            accessor: "po_number",
            header: "Purchase Order",
            filterable: true,
            filterType: "text",
        },
        {
            accessor: "supplier_name",
            header: "Supplier",
            cell: (row) => <>{row?.supplier?.supplier_name}</>,
            filterable: true,
        },
        {
            accessor: "item_name",
            header: "Item",
            cell: (row) => <>{row?.item?.item_name}</>,
            filterable: true,
        },
        {
            accessor: "delivery_date",
            header: "Delivery Date",
            cell: (row) => (
                <>{moment(row?.delivery_date).format("DD-MM-YYYY")}</>
            ),
        },
        {
            accessor: "quantity",
            header: "Requested Quantity",
        },
        {
            accessor: "received_quantity",
            header: "Received Quantity",
            cell: (row) => <>{row?.received_quantity}</>,
        },
        {
            accessor: "activity_status",
            header: "Status",
            cell: (row) => (
                <>
                    {row?.activity_status === 0 ? (
                        <span className="badge badge-dot bg-warning">
                            Pending
                        </span>
                    ) : row?.activity_status === 1 ? (
                        <span className="badge badge-dot bg-success">
                            Received
                        </span>
                    ) : (
                        <span className="badge badge-dot bg-danger">
                            Cancelled
                        </span>
                    )}
                </>
            ),
            filterable: true,
            filterType: "dropdown",
            filterOpt: TypeMasterDeliveryStatus.data
                ? TypeMasterDeliveryStatus.data?.results?.data?.map((type) => ({
                      label: type?.name,
                      value: type?.value,
                  }))
                : [],
        },
        ...(privileges.includes("allot_po_quantity")
            ? [
                  {
                      accessor: "allot_quantity",
                      header: "Receive Quantity",
                      cell: (row) => (
                          <>
                              <DeliveryStatusModal
                                  row={row}
                                  tolerance={Number(
                                      threshold.data?.results?.data?.[0]?.value
                                  )}
                              />
                          </>
                      ),
                  },
              ]
            : []),
        ...(privileges.includes("cancle_po")
            ? [
                  {
                      accessor: "cancle_po",
                      header: "Cancel PO",
                      cell: (row) => (
                          <>
                              <button
                                  className="btn btn-danger btn-sm"
                                  onClick={() =>
                                      Swal.fire({
                                          text: "Are you sure you want to cancel this purchase order?",
                                          showDenyButton: true,
                                          confirmButtonText: "Yes",
                                          denyButtonText: `No`,
                                      }).then((result) => {
                                          if (result.isConfirmed) {
                                              updatePO({
                                                  id: row?.id,
                                                  data: {
                                                      is_cancelled: true,
                                                      updated_by: userID,
                                                  },
                                              });
                                          }
                                      })
                                  }
                                  disabled={row?.activity_status === 3}
                              >
                                  Cancel
                              </button>
                          </>
                      ),
                  },
              ]
            : []),
    ];

    const handleGlobalFilterChange = (value) => {
        const q = value?.trim() || "";
        if (q !== "") {
            setGlobalFilterValue(q);
            setColumnFilterKey("");
            setColumnFilterValue("");
            setSearchParams((searchParams) => {
                searchParams.set("q", q);
                searchParams.delete("c");
                return searchParams;
            });
        } else if (q === "") {
            setGlobalFilterValue("");
            setColumnFilterKey("activity_status");
            setColumnFilterValue("0");

            setSearchParams((params) => {
                params.set("q", "0");
                params.set("c", "activity_status");
                return params;
            });
        }
    };

    const handleColumnFilterChange = (value) => {
        const q = value.map((val) => val.value?.trim() || "").join(",");
        const c = value.map((val) => val.id).join(",");
        if (q !== "" && c !== "") {
            setGlobalFilterValue("");
            setColumnFilterKey(c);
            setColumnFilterValue(q);
            setSearchParams((searchParams) => {
                searchParams.set("q", q);
                searchParams.set("c", c);
                return searchParams;
            });
        } else if (q === "" && c === "") {
            setGlobalFilterValue("");
            setColumnFilterKey("activity_status");
            setColumnFilterValue("0");

            setSearchParams((params) => {
                params.set("q", "0");
                params.set("c", "activity_status");
                return params;
            });
        }
    };

    const handlePagination = (value) => {
        setSearchParams((searchParams) => {
            searchParams.set("entries", value.pageSize);
            searchParams.set("page", value.pageIndex + 1);
            return searchParams;
        });
        setPageIndex(value.pageIndex);
        setEntries(value.pageSize);
    };

    if (errorLoadingPurchaseOrders) return <Error />;
    return (
        <>
            <div className="nk-block-head nk-block-head-sm">
                <div className="nk-block-between">
                    <div className="nk-block-head-content d-flex">
                        <h3 className="nk-block-title page-title">
                            Delivery Status
                        </h3>
                    </div>
                </div>
            </div>
            <Table
                data={purchaseOrders ? purchaseOrders?.results?.data : []}
                columns={columns}
                isLoading={
                    purchaseOrdersLoading ||
                    fetchingPurchaseOrders ||
                    updatingPO
                }
                pagination
                pageIndex={pageIndex}
                pageSize={entries}
                rowCount={purchaseOrders ? purchaseOrders?.count : 0}
                selectRows={false}
                editRows={false}
                deleteRows={false}
                // minWidth={1200}
                defaultGlobalSearchValue={
                    initialQueries.length > 0 && initialColumns.length <= 0
                        ? initialQueries[0]
                        : undefined
                }
                defaultColumnFiltersValue={
                    initialQueries.length > 0 && initialColumns.length > 0
                        ? initialColumns.map((column, index) => ({
                              id: column,
                              value: initialQueries?.[index] ?? "",
                          }))
                        : undefined
                }
                onPaginationChange={handlePagination}
                onGlobalFilterChange={handleGlobalFilterChange}
                onColumnFilterChange={handleColumnFilterChange}
            />
        </>
    );
};

export default DeliveryStatus;
